import { Role } from 'auth';
import { get } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterPermissionsQuery } from '@/modules/common/queries/permissions.ts';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import CorrespondenceTab from '@/modules/issuer-dashboard/components/holder-tabs/CorrespondenceTab.tsx';
import DetailsTab from '@/modules/issuer-dashboard/components/holder-tabs/DetailsTab.tsx';
import KYCDetails from '@/modules/issuer-dashboard/components/holder-tabs/KYCDetails.tsx';
import OverviewTab from '@/modules/issuer-dashboard/components/holder-tabs/OverviewTab.tsx';
import PaymentsTab from '@/modules/issuer-dashboard/components/holder-tabs/PaymentsTab.tsx';
import TaxReportsTab from '@/modules/issuer-dashboard/components/holder-tabs/TaxReportsTab.tsx';
import TransactionsTab from '@/modules/issuer-dashboard/components/holder-tabs/TransactionsTab.tsx';
import TransfersTab from '@/modules/issuer-dashboard/components/holder-tabs/TransfersTab.tsx';
import { useIssuerEntityBronIdKycDetailsQuery } from '@/modules/issuer-dashboard/queries/entities.ts';
import { useCreateInvestorEditMutation } from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { Badge, Button, PageLayout, Skeleton, TabItem, Tabs, TooltipNew } from 'ui';
import { ArrowLeftIcon, EditIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';
import { CreateHolderDetailsRequest } from 'api';

enum TabsEnum {
  Overview = 'Overview',
  Details = 'Details',
  Correspondence = 'Correspondence',
  Transactions = 'Transactions',
  Payments = 'Payments',
  Transfers = 'Transfers',
  KYCDetails = 'KYC Details',
  TaxReports = 'Tax Reports',
}

export const HOLDER_TAB_NAME = 'tab-holder';

const IssuerHolder = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { state, pathname } = useLocation();
  const registerId = params.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const paramTab = params.get(HOLDER_TAB_NAME);
  const isValidTab = paramTab && Object.values(TabsEnum).includes(paramTab as TabsEnum);
  const [activeTab, setActiveTab] = useState<TabsEnum>(
    isValidTab ? (paramTab as TabsEnum) : TabsEnum.Overview,
  );
  const [isDeleted, setIsDeleted] = useState<boolean | undefined>();

  useEffect(() => {
    const isValidTab = paramTab && Object.values(TabsEnum).includes(paramTab as TabsEnum);
    setActiveTab(isValidTab ? (paramTab as TabsEnum) : TabsEnum.Overview);
  }, [paramTab]);

  const { data: dataRegisterPermissions } = useRegisterPermissionsQuery();
  const {
    data: shareholderData,
    isLoading: isLoadingShareholder,
    error: shareholderError,
  } = useRegisterShareholderList(
    { entity_id: (id && +id) || 0, register_id: (registerId && +registerId) || 0 },
    { disabled: !id },
  );
  const { data: kycData, isLoading: isKycLoading } = useIssuerEntityBronIdKycDetailsQuery(
    { entity_id: (id && +id) || 0 },
    !id,
  );

  // due to backend logic with entity_id there must be one record
  const shareholder = get(shareholderData, 'shareholders.0', null);
  const entity = get(shareholderData, 'shareholders.0.entity', null);

  const isKycDoneOutsideBronId = !isKycLoading && !kycData;

  const entityName =
    'business_name_trustee' in (shareholder?.entity_details?.investor_details || {}) &&
    !!shareholder?.entity_details?.investor_details?.business_name_trustee
      ? `${shareholder?.entity_details?.investor_details?.business_name_trustee} <${entity?.entity_name}>`
      : `${entity?.entity_name}`;

  const title = (
    <div className="flex gap-3 items-center flex-wrap">
      <button
        onClick={() => {
          navigate(state?.from2 || state?.from || -1);
        }}
      >
        <img src={ArrowLeftIcon} alt="back" className="w-6 h-6 min-w-6 min-h-6" />
      </button>
      {isLoadingShareholder ? (
        <>
          <Skeleton width={100} height={30} />
          <Skeleton width={60} height={20} />
        </>
      ) : (
        <>
          <span className="text-2xl">{entityName}</span>
          <Badge containerClass="text-blue-700 bg-blue-100 font-[500] font-[Inter]">
            {fixNameEnum(entity?.registration_type)}
          </Badge>
          <span className="text-gray-500 text-sm" style={{ fontFamily: 'Inter' }}>
            {entity?.entity_id}
          </span>
        </>
      )}
    </div>
  );

  const tabsItems = useMemo<TabItem<TabsEnum>[]>(
    () => [
      {
        name: TabsEnum.Overview,
        label: 'Overview',
        container: (
          <OverviewTab
            kycLoading={isKycLoading}
            shareholder={shareholder}
            kycData={kycData?.data}
            isKycDoneOutsideBronId={isKycDoneOutsideBronId}
          />
        ),
      },
      {
        name: TabsEnum.Details,
        label: 'Details',
        container: <DetailsTab shareholder={shareholder} />,
      },
      {
        name: TabsEnum.Correspondence,
        label: 'Correspondence',
        container: <CorrespondenceTab />,
        disabled: true,
        disabledReason: 'We are working on it',
      },
      {
        name: TabsEnum.Transactions,
        label: 'Transactions',
        container: (
          <TransactionsTab
            shareholder={shareholder}
            isDeleted={isDeleted}
            setIsDeleted={setIsDeleted}
          />
        ),
      },
      {
        name: TabsEnum.Payments,
        label: 'Payments',
        container: (
          <PaymentsTab
            shareholder={shareholder}
            isDeleted={isDeleted}
            setIsDeleted={setIsDeleted}
          />
        ),
      },
      {
        name: TabsEnum.Transfers,
        label: 'Transfers',
        container: (
          <TransfersTab
            shareholder={shareholder}
            isDeleted={isDeleted}
            setIsDeleted={setIsDeleted}
          />
        ),
      },
      {
        name: TabsEnum.KYCDetails,
        label: 'KYC details',
        container: (
          <KYCDetails
            kycLoading={isKycLoading}
            kycData={kycData?.data}
            isKycDoneOutsideBronId={isKycDoneOutsideBronId}
          />
        ),
      },
      {
        name: TabsEnum.TaxReports,
        label: 'Tax reports',
        container: (
          <TaxReportsTab
            entityId={Number(id || 0)}
            registerId={Number(registerId || 0)}
            holderDisplayName={entity?.entity_name}
          />
        ),
      },
    ],
    [id, isDeleted, isKycDoneOutsideBronId, isKycLoading, kycData?.data, registerId, shareholder],
  );

  const { mutateAsync: createInvestorEdit, isLoading: isLoadingInvestorEdit } =
    useCreateInvestorEditMutation({
      onSuccess: data => {
        params.set('step', '1');
        // setParams(params);
        navigate(
          {
            pathname: issuerDashboardPaths.dynamic.updateHolder(String(data.data.id)),
            search: params.toString(),
          },
          {
            replace: false,
            state: {
              from: pathname + '?' + params.toString(),
              action: 'update',
              from2: state?.from2 || state?.from,
            },
          },
        );
      },
    });

  const handleEditHolder = useCallback(() => {
    if (!entity || !shareholder?.id) return;

    createInvestorEdit({
      shareholder_id: shareholder?.id,
      entity_data: {},
    } as CreateHolderDetailsRequest);
  }, [createInvestorEdit, entity, shareholder?.id]);

  if (
    get(shareholderError, 'response.status') === 404 ||
    shareholderData?.shareholders?.length === 0
  ) {
    // toast.error('This shareholder is not present in this register');
    return (
      <Navigate to={{ pathname: issuerDashboardPaths.static.holders, search: params.toString() }} />
    );
  }
  return (
    <PageLayout
      title={title}
      setActiveTab={setActiveTab}
      activeTab={activeTab}
      tabs={tabsItems}
      tabNameParam={HOLDER_TAB_NAME}
      headerAction={
        <div className="flex gap-2">
          <TooltipNew
            text={'This feature is not available for this issuer'}
            disabled={dataRegisterPermissions?.holder_details_permission === true}
          >
            <Button
              className="w-max whitespace-nowrap gap-2"
              onClick={handleEditHolder}
              variant="secondary"
              disabled={
                isLoadingInvestorEdit || !dataRegisterPermissions?.holder_details_permission
              }
              loading={isLoadingInvestorEdit}
            >
              <img src={EditIcon} alt="Edit" className="w-4 h-4 min-w-4 min-h-4" /> Edit information
            </Button>
          </TooltipNew>
        </div>
      }
    >
      <Tabs activeTab={activeTab} items={tabsItems} />
    </PageLayout>
  );
};

export default withPrivateAccess(IssuerHolder, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
